<script setup lang="ts">
    import { ref } from 'vue';
    import { useDropzone } from 'vue3-dropzone';
    import { useCtUploadViewModel } from '@/views/case-settings/upload/ct/useCtUploadViewModel';
    import DicomMismatchModal from '@/views/case-settings/upload/shared/DicomMismatchModal.vue';
    import UploadProgress from '@/views/case-settings/upload/ct/components/UploadProgress.vue';
    import InconsistentPatientIdentityModal
        from '@/views/case-settings/upload/ct/components/InconsistentPatientIdentityModal.vue';
    import SeriesSelectionModal from '@/views/case-settings/upload/ct/components/SeriesSelectionModal.vue';

    const upload = useCtUploadViewModel();

    const mouseOver = ref(false);
    const toggleMouseOver = (on: boolean) => () => {
        mouseOver.value = on;
        return true;
    };

    const dropzone = useDropzone({
        multiple: true,
        onDrop: upload.addFilesAndUpload,
        onDragEnter: toggleMouseOver(true),
        onDragLeave: toggleMouseOver(false),
        onDropAccepted: toggleMouseOver(false),
        onDropRejected: toggleMouseOver(false),
        disabled: upload.isCollecting,
    });
</script>

<template>
    <div class="drag-dicom-container" v-bind="dropzone.getRootProps()">
        <input v-bind="dropzone.getInputProps()" />

        <template v-if="upload.isCollecting">
            <v-progress-circular indeterminate color="primary" size="48" />
            <app-text class="mt-3">Sorting DICOM series…</app-text>
        </template>
        <template v-else>
            <p class="input-title mb-2">CT Scan</p>
            <v-icon icon="mdi-cloud-upload" color="primary" size="40"></v-icon>

            <div v-if="upload.hasScan" class="override-notice">
                <app-text class="mb-0">Overwrite existing CT Scan?</app-text>
                <app-text class="mb-0">Drag a new CT Scan (DICOM files) here.</app-text>
                <app-text class="mb-0">
                    <span class="text-error font-weight-bold">Please Note:</span>
                </app-text>
                <app-text class="mb-4">This will remove the existing scan and reprocess this Case.</app-text>
            </div>
            <app-text v-else class="mb-4 text-center">
                <b>Drag your CT Scan here.</b>
                <br />Upload a CT Scan by dragging your DICOM files here
            </app-text>
        </template>

        <InconsistentPatientIdentityModal />
        <DicomMismatchModal upload-type="ct-scan" />
        <SeriesSelectionModal />
        <UploadProgress />
    </div>
</template>

<style scoped lang="scss">
    .drag-dicom-container {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 40px;
        width: 100%;
        height: 100%;
        border-radius: 4px;
        border-width: 1px;
        border-style: dashed;
        border-color: rgb(var(--v-theme-outline));
        font-size: 18px;
        transition: border 200ms;
    }

    .override-notice {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }

    .input-title {
        font-size: 21px;
        font-weight: 700;
        color: rgb(var(--v-theme-secondary-text));
    }
</style>
